import { useState, useEffect } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import Logo from "./Logo";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/referrals", label: "Referrals" },
  { to: "/contact", label: "Contact" },
];

export const Navigation = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md border-b border-[#e6efee]" : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:bg-white focus:px-3 focus:py-2 focus:rounded">
        Skip to content
      </a>
      <div className="container-x flex items-center justify-between h-16 lg:h-20">
        <Logo />
        <nav aria-label="Primary" className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-[15px] font-medium transition-colors ${
                  isActive ? "text-[#0B6E70] bg-[#E6FFFA]" : "text-[#1A202C] hover:text-[#0B6E70]"
                }`
              }
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <a href="tel:0426635885" data-testid="nav-call-btn" className="btn-ghost text-sm">
            <Phone className="h-4 w-4" /> 0426 635 885
          </a>
          <Link to="/referrals" data-testid="nav-enquire-btn" className="btn-primary text-sm">
            Make an enquiry
          </Link>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#e6efee] bg-white text-[#0F2A2C]"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-[#e6efee] bg-white">
          <div className="container-x py-4 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base font-medium ${isActive ? "text-[#0B6E70] bg-[#E6FFFA]" : "text-[#1A202C]"}`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/referrals" data-testid="mobile-enquire-btn" className="btn-primary mt-2">
              Make an enquiry
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navigation;
